import React, { forwardRef, useImperativeHandle, useEffect, useMemo } from "react";
import { Col, Row } from "reactstrap";
import SelectBox from "../../../components/SelectBox";
import { ASSIGN_QC_ACTIONS } from "../../../utils/Constant";
import SearchBox from "../../../components/SearchBox";
import { useForm, Controller } from "react-hook-form";
import { SelectOptionType } from "../../../models/GlobalModels";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { LoggedUserType } from "../../../models/GlobalModels";
import { loginSelector } from "../../../redux/slices/loginSlice";
import { reassignQCApi, assignQCApi } from "../../../api/icvActivityAPI";

type FormValues = {
    selectedQC: SelectOptionType | null;
    reassignQCRemarks: string;
};

const ICVAssignQC = forwardRef(({
    action,
    setIsFormValid,
    selectedICVActivities,
    onSuccess
}: any, ref) => {
    const user: LoggedUserType = useSelector(loginSelector).user;
    const dispatch: any = useDispatch();
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isValid },
    } = useForm<FormValues>({
        defaultValues: {
            selectedQC: null,
            reassignQCRemarks: '',
        },
        mode: 'all',
    });

    const {
        storeICVGetQCUsersData,
        storeICVReassignQC,
        storeICVAssignQC
    } = useSelector((state: any) => state.icvActivity);
    
    const qcExecutiveList = storeICVGetQCUsersData?.data || [];

    // Filter out QCs that are already assigned to selected records in reassign scenario
    const filteredQCExecutiveList = useMemo(() => {
        if (action === ASSIGN_QC_ACTIONS.REASSIGN && selectedICVActivities?.length > 0) {
            // Get all QC emails that are already assigned to the selected records
            const assignedQC = selectedICVActivities
                .map((record: any) => record.qcName)
                .filter((name: string) => name && name.trim() !== '');

            // Filter out QCs that are already assigned
            return qcExecutiveList.filter((qc: any) => 
                !assignedQC.includes(qc.label)
            );
        }
        return qcExecutiveList;
    }, [action, selectedICVActivities, qcExecutiveList]);

    const onSubmit = (data: FormValues) => {
        if(action === ASSIGN_QC_ACTIONS.ASSIGN) {
            dispatch(assignQCApi(
                selectedICVActivities.map((record: any) => record.icvId),
                data?.selectedQC?.email ?? '',
                data?.selectedQC?.label ?? '',
                user?.emailId,
            ))
        }
        else if (action === ASSIGN_QC_ACTIONS.REASSIGN) {
            dispatch(reassignQCApi(
                selectedICVActivities.map((record: any) => record.icvId),
                data?.selectedQC?.email ?? '',
                data?.selectedQC?.label ?? '',
                user?.emailId,
                data?.reassignQCRemarks
            ))
        }
    }

    useImperativeHandle(ref, () => ({
        submitForm: () => {
            handleSubmit(onSubmit)();
        },
        isValid: isValid
    }));

    useEffect(() => {
        if (setIsFormValid) {
            setIsFormValid(isValid);
        }
    }, [isValid, setIsFormValid]);

    useEffect(() => {
        if(storeICVAssignQC && storeICVAssignQC.statusCode === 1){
            onSuccess && onSuccess();
        }
    },[storeICVAssignQC])

    
    useEffect(() => {
        if(storeICVReassignQC && storeICVReassignQC.statusCode === 1){
            onSuccess && onSuccess();
        }
    },[storeICVReassignQC])

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Row className="mb-2">
                    <Col md={6}>
                        <Controller
                            name="selectedQC"
                            control={control}
                            rules={{ required: "QC Executive is required" }}
                            render={({ field, fieldState: { error } }) => (
                                <SelectBox
                                    label="QC Executive"
                                    options={filteredQCExecutiveList}
                                    className="css-select-bg"
                                    onChange={(value) => field.onChange(value)}
                                    placeholder="Select Name"
                                    value={field.value}
                                    required={true}
                                />
                            )}
                        />
                    </Col>
                </Row>
                {action === ASSIGN_QC_ACTIONS.REASSIGN &&
                    <Row className="mb-2">
                        <Col md={12}>
                            <Controller
                                name="reassignQCRemarks"
                                control={control}
                                render={({ field }) => (
                                    <SearchBox
                                        label="Remarks (optional)"
                                        placeHolder={""}
                                        onChange={(value) => field.onChange(value)}
                                        value={field.value}
                                    />
                                )}
                            />
                        </Col>
                    </Row>
                }
            </form>
        </>
    );
});

export default ICVAssignQC;
